import { useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import CompanyHeader from '@/components/company/CompanyHeader';
import FormBuilderWithLayout from '@/components/company/FormBuilderWithLayout';
import JobDescriptionFilters from './JobDescriptionFilters';
import JobDescriptionTable from './JobDescriptionTable';
import useJobDescriptionHandlers from '@/hooks/useJobDescriptionHandler';
import { IJobDescription } from '@/states/job.state';
import useKeyActivityState from '@/states/key-activities.state';

export default function JobDescriptionContent() {
  const { handleSubmit, reset } = useFormContext<IJobDescription>();
  const formRef = useRef<HTMLFormElement>(null);

  const { keyActivities } = useKeyActivityState();

  const keyActivityOptions = keyActivities.map(ka => ({
    label: ka.name || '-',
    value: ka.id.toString(),
  }));

  const {
    isLoading,
    allPublished,
    formMode,
    orgCharts,
    fields,
    displayData,
    pageConfig,
    setPageConfig,
    handleSave,
    handleDelete,
    setFormMode,
    setSelectedRow,
    searchTerm,
    setSearchTerm,
    selectedRole,
    setSelectedRole,
    selectedStatus,
    setSelectedStatus,
    selectedKeyActivity,
    setSelectedKeyActivity,

    roleOptions,
    handleAllPublish,
    fetchData,
  } = useJobDescriptionHandlers({ reset });

  return (
    <section className="w-full">
      <CompanyHeader
        title="Job Descriptions"
        description="Define detailed job responsibilities and performance indicators."
        onCreate={() => {
          reset({});
          setFormMode('add');
        }}
        isPublished={allPublished}
        onPublish={async val => {
          await handleAllPublish(val);
          await fetchData();
        }}
      />

      <div className="mx-5 border-b border-gray-200" />

      {formMode && orgCharts.length > 0 && (
        <form ref={formRef} onSubmit={handleSubmit(handleSave)} className="mt-4">
          <FormBuilderWithLayout
            description={formMode === 'add' ? 'Create Job Description' : 'Edit Job Description'}
            fields={fields}
            onSubmit={handleSubmit(handleSave)}
            onCancel={() => {
              setFormMode(null);
              setSelectedRow(null);
            }}
            submitLabel={formMode === 'add' ? 'Save' : 'Update'}
            cancelLabel="Cancel"
            showPublishButton={true}
          />
        </form>
      )}

      <JobDescriptionFilters
        searchTerm={searchTerm}
        setSearchTerm={val => {
          setSearchTerm(val);
          setPageConfig(prev => ({ ...prev, page: 0 }));
        }}
        selectedRole={selectedRole}
        setSelectedRole={val => {
          setSelectedRole(val);
          setPageConfig(prev => ({ ...prev, page: 0 }));
        }}
        selectedStatus={selectedStatus}
        setSelectedStatus={val => {
          setSelectedStatus(val);
          setPageConfig(prev => ({ ...prev, page: 0 }));
        }}
        selectedKeyActivity={selectedKeyActivity}
        setSelectedKeyActivity={val => {
          setSelectedKeyActivity(val);
          setPageConfig(prev => ({ ...prev, page: 0 }));
        }}
        roleOptions={roleOptions}
        keyActivityOptions={keyActivityOptions}
      />

      <JobDescriptionTable
        isLoading={isLoading}
        paginatedData={displayData}
        pageConfig={pageConfig}
        setPageConfig={setPageConfig}
        onEdit={row => {
          reset(row);
          setFormMode('edit');
          setSelectedRow(row);
        }}
        onDelete={handleDelete}
      />
    </section>
  );
}
